/*
 * Firebase Storage compatibility facade.
 *
 * Existing callers keep using ref/uploadBytes/getDownloadURL/deleteObject.
 * In Online Only mode those calls are untouched Firebase SDK calls. In
 * Hybrid/Offline Server modes, uploads are stored on the paired LAN server
 * first and later synchronized to Firebase Storage by the server queue.
 */
import {
  ref as firebaseRef,
  uploadBytes as firebaseUploadBytes,
  getDownloadURL as firebaseGetDownloadURL,
  deleteObject as firebaseDeleteObject,
} from 'firebase/storage';
import serverModeService, { SERVER_MODES } from '../services/serverModeService';

const localUrls = new Map();
const keyOf = (reference) => String(reference?.fullPath || reference?._location?.path_ || '');
const localModeRequired = () => serverModeService.getSnapshot()?.configuredMode === SERVER_MODES.OFFLINE_SERVER_ONLY;
const shouldTryLocal = () => {
  const snapshot = serverModeService.getSnapshot();
  return snapshot?.localConfigured === true && snapshot?.effectiveMode !== SERVER_MODES.ONLINE_ONLY;
};

export const ref = (...args) => firebaseRef(...args);

export const uploadBytes = async (reference, data, metadata) => {
  const storagePath = keyOf(reference);
  if (storagePath && shouldTryLocal()) {
    try {
      const local = await serverModeService.uploadFile({ storagePath, file: data, metadata: metadata || {} });
      if (local?.localUrl) localUrls.set(storagePath, local.localUrl);
      return {
        ref: reference,
        metadata: { ...(metadata || {}), fullPath: storagePath, size: Number(local?.size || data?.size || 0), local: true },
        localUrl: local?.localUrl || '',
      };
    } catch (error) {
      if (localModeRequired() || (typeof navigator !== 'undefined' && navigator.onLine === false)) throw error;
      // Hybrid preserves the original cloud behavior when the LAN server is
      // unavailable and cloud fallback is still possible.
    }
  }
  return firebaseUploadBytes(reference, data, metadata);
};

export const getDownloadURL = async (reference) => {
  const storagePath = keyOf(reference);
  if (storagePath && localUrls.has(storagePath)) return localUrls.get(storagePath);
  return firebaseGetDownloadURL(reference);
};

export const deleteObject = async (reference) => {
  const storagePath = keyOf(reference);
  if (storagePath && shouldTryLocal()) {
    try {
      await serverModeService.deleteFile(storagePath);
      localUrls.delete(storagePath);
      return;
    } catch (error) {
      if (localModeRequired() || (typeof navigator !== 'undefined' && navigator.onLine === false)) throw error;
    }
  }
  return firebaseDeleteObject(reference);
};
